import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { LINK_MAX_LENGTH } from '../common';

/** Evento del editor con el que se avisa de un enlace pegado que no se ha podido poner. */
export const LINK_PASTE_HINT_EVENT = 'newsLinkPasteHint';

export const LINK_TOO_LONG_HINT = `El enlace pegado supera los ${LINK_MAX_LENGTH} caracteres y no se ha añadido. Usa uno más corto.`;
export const LINK_DOES_NOT_FIT_HINT =
    'La URL no cabe como texto. Escribe un texto corto (por ejemplo, «Ver candidatos»), selecciónalo y pega la URL encima: quedará como enlace.';

const SINGLE_URL = /^https?:\/\/\S+$/i;

/**
 * Pegar una URL sola (`linkOnPaste` de @tiptap/extension-link está apagado, ver `RichBodyInput`):
 *
 * - con texto seleccionado, ese texto pasa a ser el enlace y la URL no cuenta como texto visible;
 * - sin selección, se pega como enlace si cabe en el límite de caracteres visibles;
 * - si no cabe, o pasa de `LINK_MAX_LENGTH`, no se pega recortada: se avisa con
 *   `LINK_PASTE_HINT_EVENT` para que el formulario explique cómo enlazarla.
 *
 * Cualquier otra cosa pegada sigue el camino normal (recortada por `MaxVisibleLength`).
 */
export const PasteLink = Extension.create({
    name: 'pasteLink',

    addOptions() {
        return { limit: 600 };
    },

    addProseMirrorPlugins() {
        const { editor } = this;
        const { limit } = this.options;

        return [
            new Plugin({
                key: new PluginKey('pasteLink'),
                props: {
                    handlePaste: (view, event, slice) => {
                        // El texto del portapapeles; si no llega (pegados sintéticos), el del fragmento.
                        const text = event.clipboardData?.getData('text/plain') ?? slice.content.textBetween(0, slice.content.size, '\n', '\n');
                        const url = (text || '').trim();
                        if (!SINGLE_URL.test(url)) return false;

                        if (url.length > LINK_MAX_LENGTH) {
                            editor.emit(LINK_PASTE_HINT_EVENT, LINK_TOO_LONG_HINT);
                            return true;
                        }

                        const { from, to, empty } = view.state.selection;
                        if (!empty && editor.can().setLink({ href: url })) {
                            editor.chain().setLink({ href: url }).run();
                            return true;
                        }

                        const selected = view.state.doc.textBetween(from, to, '', '').length;
                        if (view.state.doc.textContent.length - selected + url.length > limit) {
                            editor.emit(LINK_PASTE_HINT_EVENT, LINK_DOES_NOT_FIT_HINT);
                            return true;
                        }

                        editor
                            .chain()
                            .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] })
                            .unsetMark('link')
                            .run();
                        return true;
                    },
                },
            }),
        ];
    },
});
